// The login backdrops, ported from the old Haxe `GGBase` + `Winter` / `Fall`
// classes: a tree on the right, a hiker on the left, and a seasonal layer.
// Which season shows is chosen in LoginPage (the old code switched it by
// hand too).

export type Season = "winter" | "fall";

type Flake = { x: number; y: number; v: number; r: number };
type Line = { x1: number; y1: number; x2: number; y2: number; w: number; c: number };

const TREE_RIGHT = 550;
const PI2 = 0.01745329251;
// The hiker SVG is 199x363; drawn a little smaller than the original (Aaron, 2026-10-11).
const MAN_SCALE = 0.85;
const MAN_W = Math.round(199 * MAN_SCALE);
const MAN_H = Math.round(363 * MAN_SCALE);

function load(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

/** Scatter points along a line (the old "lines" loop): calls `draw(x, y)` per point. */
function alongLines(canvas: HTMLCanvasElement, lines: Line[], treeRight: number, draw: (x: number, y: number, count: number) => void) {
  for (const l of lines) {
    const m = (l.y1 - l.y2) / (l.x1 - l.x2);
    const b = l.y1 - m * l.x1;
    const w2 = l.w / 2;
    for (let count = 0; count < l.c; count++) {
      let x = Math.random() * (l.x2 - l.x1) + l.x1;
      let y = canvas.height - (m * x + b);
      if (count % 5 === 0) {
        let amt = w2;
        if (count % 10 === 0) amt = -amt;
        y += Math.random() * (amt / 2);
      }
      x -= Math.random() * l.w - w2;
      y -= Math.random() * l.w - w2;
      x += canvas.width - treeRight;
      draw(x, y, count);
    }
  }
}

abstract class Scene {
  protected ctx: CanvasRenderingContext2D;
  protected tree?: HTMLImageElement;
  protected man?: HTMLImageElement;
  protected treeRight = TREE_RIGHT;
  private resizeTimer?: number;
  private onResize = () => this.scheduleResize();

  constructor(protected canvas: HTMLCanvasElement) {
    this.ctx = canvas.getContext("2d")!;
  }

  async start() {
    [this.tree, this.man] = await Promise.all([load("/assets/ui/tree.svg"), load("/assets/ui/man.svg")]);
    await this.init();
    this.render();
    window.addEventListener("resize", this.onResize);
  }

  stop() {
    window.removeEventListener("resize", this.onResize);
  }

  protected async init() {}

  private scheduleResize() {
    if (this.resizeTimer) return;
    this.resizeTimer = window.setTimeout(() => {
      this.resizeTimer = undefined;
      this.render();
    }, 500);
  }

  /** Tree, hiker, then the season's own layer. */
  protected render() {
    const { canvas, ctx } = this;
    const bHeight = document.body.offsetHeight;
    const bWidth = document.body.offsetWidth;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    canvas.height = bHeight;
    canvas.width = bWidth;
    this.treeRight = TREE_RIGHT;
    let manX = 100;
    let manY = bHeight - MAN_H;
    let treeX = bWidth - 2604;
    if (canvas.width < 600) {
      manX = 0;
      manY += 50;
      this.treeRight = TREE_RIGHT - 150;
      treeX += 150;
    }
    ctx.drawImage(this.tree!, treeX, bHeight - 735, 2604, 735);
    ctx.drawImage(this.man!, manX, manY, MAN_W, MAN_H);
    this.renderSeason();
  }

  protected abstract renderSeason(): void;
}

/** Snow banks drawn as scattered flakes, plus two layers of falling snow. */
export class WinterScene extends Scene {
  private background = document.createElement("canvas");
  private bctx = this.background.getContext("2d")!;
  private flake?: HTMLImageElement;
  private back: Flake[] = [];
  private front: Flake[] = [];
  private timer?: number;
  private ready = false;
  private static LINES: Line[] = [
    { x1: 130, y1: 25, x2: 540, y2: 90, w: 10, c: 260 },
    { x1: 260, y1: 35, x2: 340, y2: 105, w: 5, c: 100 },
    { x1: -700, y1: 33, x2: -340, y2: 70, w: 7, c: 180 },
    { x1: -340, y1: 70, x2: -200, y2: 45, w: 7, c: 100 },
  ];

  constructor(
    canvas: HTMLCanvasElement,
    private maxBack = 400,
    private maxFront = 300,
  ) {
    super(canvas);
  }

  protected override async init() {
    this.flake = await load("/assets/ui/flake.svg");
    this.timer = window.setInterval(() => this.tick(), 50);
  }

  override stop() {
    super.stop();
    if (this.timer) window.clearInterval(this.timer);
    this.timer = undefined;
  }

  protected renderSeason() {
    const { canvas } = this;
    alongLines(canvas, WinterScene.LINES, this.treeRight, (x, y) => {
      this.drawFlake(x, y, Math.round(Math.random() * 15) + 3, Math.random() * 360);
    });
    this.background.width = canvas.width;
    this.background.height = canvas.height;
    this.bctx.drawImage(canvas, 0, 0, canvas.width, canvas.height);
    this.ready = true;
  }

  private tick() {
    if (!this.ready) return;
    const { canvas, ctx } = this;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    for (let i = this.back.length - 1; i >= 0; i--) {
      const f = this.back[i];
      f.y += f.v;
      if (f.y > canvas.height) this.back.splice(i, 1);
      else this.drawDot(f);
    }
    const bNew = this.maxBack - this.back.length;
    for (let a = 0; a < bNew; a++) {
      const r = Math.random() * 2 + 1;
      const f = { x: Math.random() * canvas.width, y: bNew === this.maxBack ? Math.random() * canvas.height : -Math.random() * 100, r, v: r * 0.5 + Math.random() * 0.2 };
      this.back.push(f);
      this.drawDot(f);
    }
    ctx.drawImage(this.background, 0, 0, canvas.width, canvas.height);
    for (let i = this.front.length - 1; i >= 0; i--) {
      const f = this.front[i];
      f.y += f.v;
      if (f.y > canvas.height) this.front.splice(i, 1);
      else this.drawFlake(f.x, f.y, f.r, 0);
    }
    const fNew = this.maxFront - this.front.length;
    for (let a = 0; a < fNew; a++) {
      const s = Math.round(Math.random() * 15) + 3;
      const f = { x: Math.random() * canvas.width, y: fNew === this.maxFront ? Math.random() * canvas.height : -Math.random() * 100, r: s, v: s * 0.1 + Math.random() * 0.2 };
      this.front.push(f);
      this.drawFlake(f.x, f.y, f.r, 0);
    }
  }

  private drawFlake(x: number, y: number, s: number, r: number) {
    const { ctx } = this;
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(r * PI2);
    ctx.drawImage(this.flake!, 0, 0, s, s);
    ctx.restore();
  }

  private drawDot(f: Flake) {
    const { ctx } = this;
    ctx.beginPath();
    ctx.arc(f.x, f.y, f.r, 0, Math.PI * 2);
    ctx.fillStyle = "rgba(255,255,255,0.6)";
    ctx.fill();
  }
}

// --- wind -------------------------------------------------------------------
// Smooth value noise, the usual way procedural wind is built: a slow 1-D noise
// over time gives gusts that build and die away; a 2-D noise over space and time
// gives local turbulence so leaves near each other do not move in lockstep.

function hash(ix: number, iy: number): number {
  let h = (ix * 374761393 + iy * 668265263) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967295;
}

function smooth(t: number): number {
  return t * t * (3 - 2 * t);
}

/** 2-D value noise in [0,1). */
function noise2(x: number, y: number): number {
  const ix = Math.floor(x);
  const iy = Math.floor(y);
  const fx = smooth(x - ix);
  const fy = smooth(y - iy);
  const a = hash(ix, iy);
  const b = hash(ix + 1, iy);
  const c = hash(ix, iy + 1);
  const d = hash(ix + 1, iy + 1);
  return a + (b - a) * fx + (c - a) * fy + (a - b - c + d) * fx * fy;
}

/** Two octaves of noise, in [-1,1]. */
function fbm(x: number, y: number): number {
  return (noise2(x, y) * 0.7 + noise2(x * 2.1 + 17, y * 2.1 + 31) * 0.3) * 2 - 1;
}

class Wind {
  /** Mean speed leftwards (px/s) and how strong gusts get on top of it. */
  constructor(
    private mean = 70,
    private gust = 160,
  ) {}

  /** Gust level in [0,1]: slow, with the occasional strong one. */
  level(t: number): number {
    const g = (fbm(t * 0.12, 7.3) + 1) / 2; // slow swells
    return g * g; // mostly calm, sometimes strong
  }

  /** Wind vector (px/s) at a point: leftwards with gusts, plus local turbulence. */
  at(x: number, y: number, t: number): { x: number; y: number } {
    const level = this.level(t);
    const speed = this.mean + this.gust * level;
    const tx = fbm(x * 0.004 + t * 0.25, y * 0.004);
    const ty = fbm(x * 0.004 + 53, y * 0.004 - t * 0.2);
    return { x: -speed * (1 + 0.35 * tx), y: 25 * ty * (0.5 + level) };
  }
}

// --- leaves -----------------------------------------------------------------

type Leaf = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  rot: number; // degrees, in the plane of the page
  spin: number; // degrees/s
  /** rotation about an axis in the page: the leaf's face turns toward and away from the viewer */
  pitch: number; // degrees
  pitchRate: number; // degrees/s
  size: number;
  color: number[];
  alpha: number;
  /** the spot of ground this leaf will settle on */
  groundY: number;
  /** wind speed that gets it airborne again once it is resting */
  liftAt: number;
  /** when it landed (resting leaves get restless after a while) */
  landedAt: number;
  /** when it last took off: the peel-off is helped along for a moment after this */
  liftedAt: number;
  /** a neighbour's take-off scheduled this one to follow at this time (0 = none) */
  liftPending: number;
  bornAt: number;
};

/**
 * Fallen leaves under the tree (as the old static scene drew them), plus a
 * few live leaves. Each one is a small body in the wind field: drag pulls it
 * toward the air speed (more when it faces the wind), gravity pulls it down,
 * a lift term that depends on its angle makes it swing and flutter as it falls
 * (the fluttering/tumbling regimes of a falling plate), and the ground is just
 * friction — so landing and taking off are continuous, never a jump.
 */
export class FallScene extends Scene {
  private static LINES: Line[] = [
    { x1: 130, y1: 20, x2: 540, y2: 90, w: 20, c: 160 },
    { x1: 105, y1: 370, x2: 465, y2: 390, w: 200, c: 680 },
  ];
  private static COLORS = [
    [189, 17, 9],
    [189, 17, 9],
    [189, 17, 9],
    [196, 120, 33],
  ];
  private static LEAF = [
    28.35, 1, 28.3, 4.2, 18.1, 3.5, 14.8, 6.9, 5.127, 8.6, 10.574, 11.0, 16.1, 16.6, 8.1, 21.5, 5.1, 22, 3.3, 22, 7.9, 24.3, 8.2, 29.0, 7.6, 30.8, 3.693, 39.1, 15.09,
    36.91, 18.172, 35.7635, 22.831, 33.2545, 24.623, 35.26006, 24.551, 41.8554, 22.975, 47.876, 21.469, 50.385, 26.773, 50.385, 28.995, 54.113, 28.494, 60.25,
    35.303, 53.468, 36.952, 50.38506, 40.536, 50.17, 44.549, 54.399, 43.259, 49.669, 43.689, 36.981, 46.341, 38.057, 52.076, 45.368, 52.863, 47.7334, 54.083,
    45.583, 60.893, 48.594, 65.408, 52.822, 63.473, 48.594, 63.329, 45.368, 63.4, 38.415, 66.053, 35.4774, 70.999, 34.545, 65.838, 32.753, 62.469, 29.814,
    58.312, 23.076, 65.121, 19.494, 72.074, 17.9872, 52.721, 10.39, 51.789, 7.8803, 53.868, 4.942, 38.958, 9.6732,
  ];
  private static MAX_AIRBORNE = 12;
  private static MAX_RESTING = 14;
  private static GRAVITY = 110; // px/s², small: a leaf falls slowly

  private leafBuffers = new Map<string, HTMLCanvasElement>();
  private background = document.createElement("canvas");
  private bctx = this.background.getContext("2d")!;
  private wind = new Wind();
  private leaves: Leaf[] = [];
  private frame?: number;
  private last = 0;
  private ready = false;
  private time = 0;
  private nextSpawnAt = 0;

  protected override async init() {
    this.last = performance.now();
    const loop = (now: number) => {
      this.tick(Math.min(0.05, (now - this.last) / 1000));
      this.last = now;
      this.frame = window.requestAnimationFrame(loop);
    };
    this.frame = window.requestAnimationFrame(loop);
  }

  override stop() {
    super.stop();
    if (this.frame) window.cancelAnimationFrame(this.frame);
    this.frame = undefined;
  }

  /** One tinted leaf per colour+alpha step, cached. */
  private leaf(color: number[], a: number): HTMLCanvasElement {
    const key = `${color.join(",")}/${a}`;
    let buffer = this.leafBuffers.get(key);
    if (buffer) return buffer;
    buffer = document.createElement("canvas");
    buffer.width = 72;
    buffer.height = 60;
    const bx = buffer.getContext("2d")!;
    bx.fillStyle = `rgba(${color[0]},${color[1]},${color[2]},${a})`;
    bx.beginPath();
    const p = FallScene.LEAF;
    bx.moveTo(p[0], p[1]);
    for (let i = 2; i < p.length; i += 2) bx.lineTo(p[i], p[i + 1]);
    bx.closePath();
    bx.fill();
    this.leafBuffers.set(key, buffer);
    return buffer;
  }

  private drawLeaf(x: number, y: number, size: number, rot: number, color: number[], alpha: number, pitch = 0) {
    const { ctx } = this;
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(rot * PI2);
    // fake the third dimension: foreshorten across the pitch axis, and darken the
    // leaf as it turns edge-on and shows its back
    const c = Math.cos(pitch * PI2);
    const sx = Math.sign(c || 1) * Math.max(0.06, Math.abs(c));
    ctx.scale(sx, 1);
    ctx.globalAlpha = alpha * (0.55 + 0.45 * Math.abs(c));
    ctx.drawImage(this.leaf(color, 1), -size / 2, -size / 2, size, size);
    ctx.restore();
  }

  protected renderSeason() {
    const { canvas } = this;
    alongLines(canvas, FallScene.LINES, this.treeRight, (x, y) => {
      const color = FallScene.COLORS[Math.round(Math.random() * (FallScene.COLORS.length - 1))];
      const size = Math.round(Math.random() * 15) + 3;
      const a = Math.round((Math.random() * 0.2 + 0.8) * 20) / 20;
      this.drawLeaf(x, y, size, Math.random() * 360, color, a);
    });
    this.background.width = canvas.width;
    this.background.height = canvas.height;
    this.bctx.drawImage(canvas, 0, 0, canvas.width, canvas.height);
    this.ready = true;
    // the ground moved with the resize
    for (const l of this.leaves) l.groundY = Math.min(l.groundY, canvas.height - 6);
  }

  private spawn() {
    const { canvas } = this;
    const w = this.wind.at(canvas.width, canvas.height / 2, this.time);
    this.leaves.push({
      x: canvas.width + 20,
      // never above the tree: the canopy top sits about 500 px above the page bottom
      y: Math.max(10, canvas.height - 500) + Math.random() * Math.min(canvas.height - 70, 440),
      vx: w.x * (0.6 + Math.random() * 0.4),
      vy: 10 + Math.random() * 20,
      rot: Math.random() * 360,
      spin: (Math.random() - 0.5) * 240,
      pitch: Math.random() * 360,
      pitchRate: (Math.random() - 0.5) * 400,
      size: 9 + Math.random() * 9,
      color: FallScene.COLORS[Math.round(Math.random() * (FallScene.COLORS.length - 1))],
      alpha: 0.85 + Math.random() * 0.15,
      groundY: canvas.height - 6 - Math.random() * 46,
      liftAt: 90 + Math.random() * 80,
      landedAt: 0,
      liftedAt: -10,
      liftPending: 0,
      bornAt: this.time,
    });
  }

  /** Peel a resting leaf off the ground: it skids along with the wind first, then climbs. */
  private liftOff(l: Leaf, wx: number) {
    l.vy = -(2 + Math.random() * 3);
    l.vx = wx * (0.4 + Math.random() * 0.2);
    l.spin = (Math.random() - 0.5) * 120;
    l.pitchRate = (Math.random() - 0.5) * 160;
    l.y = l.groundY - 0.6;
    l.liftedAt = this.time;
    l.liftPending = 0;
    l.groundY = this.canvas.height - 6 - Math.random() * 46;
    l.liftAt = 90 + Math.random() * 80;
    // leaves resting close by tend to go with it, a beat later
    for (const o of this.leaves) {
      if (o === l || o.liftPending || o.y < o.groundY - 0.5) continue;
      const d = Math.hypot(o.x - l.x, o.y - l.y);
      if (d < 55 && Math.random() < 0.75) o.liftPending = this.time + 0.05 + Math.random() * 0.45;
    }
  }

  private tick(dt: number) {
    if (!this.ready) return;
    const { canvas, ctx } = this;
    this.time += dt;

    let airborne = 0;
    let resting = 0;
    for (const l of this.leaves) {
      if (l.y >= l.groundY - 0.5) resting++;
      else airborne++;
    }
    if (this.time >= this.nextSpawnAt) {
      if (airborne < FallScene.MAX_AIRBORNE) this.spawn();
      this.nextSpawnAt = this.time + 0.5 + Math.random() * 2;
    }
    // keep the ground from filling up: the oldest resting leaf becomes easy to lift
    if (resting > FallScene.MAX_RESTING) {
      const oldest = this.leaves.filter((l) => l.y >= l.groundY - 0.5).sort((a, b) => a.bornAt - b.bornAt)[0];
      if (oldest) oldest.liftAt = 0;
    }

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(this.background, 0, 0, canvas.width, canvas.height);

    for (let i = this.leaves.length - 1; i >= 0; i--) {
      const l = this.leaves[i];
      const w = this.wind.at(l.x, l.y, this.time);
      const onGround = l.y >= l.groundY - 0.5;
      // relative air flow over the leaf
      const ux = w.x - l.vx;
      const uy = w.y - l.vy;
      const speed = Math.hypot(ux, uy);
      const facing = Math.abs(Math.cos(l.pitch * PI2)); // 1 = face to the flow, 0 = edge-on
      let ax = 0;
      let ay = FallScene.GRAVITY;
      if (!onGround) {
        // drag toward the air speed, stronger when broadside
        const drag = 1.2 + 2.8 * facing;
        ax += ux * drag;
        ay += uy * drag;
        // just after take-off the ground eddy carries it: the help starts near zero (it skids
        // along first), builds to a peak around 0.7 s, then fades — so it goes out, then up
        const sinceLift = this.time - l.liftedAt;
        if (sinceLift < 1.4) {
          const climb = Math.sin((Math.PI * sinceLift) / 1.4);
          ay -= (FallScene.GRAVITY + 120 + Math.abs(w.x) * 0.5) * climb;
          ax += w.x * 1.5 * (1 - climb); // and it is dragged along the ground line at the start
        }
        // lift across the flow, flipping sign with the leaf's angle: the side-to-side swing of a falling leaf
        const lift = 0.9 * speed * Math.sin(2 * l.rot * PI2);
        ax += (-uy / (speed + 1)) * lift;
        ay += (ux / (speed + 1)) * lift;
        // the flow tumbles it: it flips about its own axis (pitch) faster in faster air, and
        // the fluttering lift swings it in the plane (spin); both ease back when the air is still
        l.pitchRate += (speed * 2.2 * (1 + 0.5 * Math.sin(l.rot * PI2)) * Math.sign(l.pitchRate || 1) - l.pitchRate * 0.8) * dt * 2;
        l.spin += (speed * 1.2 * Math.cos(l.rot * PI2) - l.spin * 0.9) * dt * 2;
        l.vx += ax * dt;
        l.vy += ay * dt;
        l.x += l.vx * dt;
        l.y += l.vy * dt;
        l.rot += l.spin * dt;
        l.pitch += l.pitchRate * dt;
        if (l.y >= l.groundY) {
          // touch down: no jump, just the ground taking the vertical speed away
          l.y = l.groundY;
          l.vy = 0;
          l.landedAt = this.time;
        }
      } else {
        // resting on the ground: friction, a slight stir in gusts, and lift-off when the wind is strong enough
        const windSpeed = Math.abs(w.x);
        const friction = Math.exp(-6 * dt);
        l.vx = l.vx * friction + (windSpeed > l.liftAt * 0.6 ? w.x * 0.05 * dt : 0);
        l.x += l.vx * dt;
        // settle flat, face up
        const flat = Math.round(l.rot / 180) * 180;
        l.rot += (flat - l.rot) * Math.min(1, dt * 3);
        const faceUp = Math.round(l.pitch / 360) * 360;
        l.pitch += (faceUp - l.pitch) * Math.min(1, dt * 4);
        l.pitchRate *= friction;
        l.spin *= friction;
        // a gust above this leaf's threshold lifts it; after a long rest even a breeze will do
        const restless = this.time - l.landedAt > 6 + (l.bornAt % 10);
        const threshold = restless ? l.liftAt * 0.5 : l.liftAt;
        const follow = l.liftPending > 0 && this.time >= l.liftPending;
        if (follow || (windSpeed > threshold && Math.random() < dt * (windSpeed - threshold) / 50)) {
          // gust strong enough (or a neighbour went): it peels off the ground and the air takes over
          this.liftOff(l, w.x);
        }
      }
      if (l.x < -30 || l.y > canvas.height + 20) {
        this.leaves.splice(i, 1);
        continue;
      }
      this.drawLeaf(l.x, l.y, l.size, l.rot, l.color, l.alpha, l.pitch);
    }
  }
}

export function createScene(season: Season, canvas: HTMLCanvasElement): Scene {
  return season === "fall" ? new FallScene(canvas) : new WinterScene(canvas);
}
